/**
 * index.tsx
 * [Readme] --------------------------------------------------------------------------------------
 * 
 * 이미지 
 * 
 * -----------------------------------------------------------------------------------------------
 */

import React, { useEffect } from 'react';
import { useState } from 'react';
import Popup from 'common-modules/general/Common/Components/Popup/Popup';
import styled, { css } from 'styled-components';
import { GetStyledCompImg } from 'common-modules/general/Modules/functions';
import { observer, useLocalObservable } from 'mobx-react';
import ImgZoomModel from 'common-modules/general/Model/ImgZoomModel';

const ImgContainer = styled.div<{ isZoom: boolean; x: number; y: number }>`
  text-align: center;
  display: table-cell;
  vertical-align: top;
  max-width: auto;
  width: 100%;
  height: -webkit-calc(100vh - 260px);
  height: calc(100vh - 260px);
  ${props =>
    props.isZoom &&
    css`
      transform: translate3d(0px, 0px, 0px) scale(2, 2);
      position: relative;
    `};

  ${props =>
    props.isZoom &&
    props.x &&
    props.y &&
    css`
      transform: translate3d(${props.x}px, ${props.y}px, 0px) scale(2, 2);
    `}
`;

const Img = styled.img`
  height: 100%;
  -webkit-user-drag: none;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
`;

const Container = styled.div<{ isZoom: boolean }>`
  cursor: ${props => (props.isZoom ? 'zoom-out' : 'zoom-in')};
  overflow: hidden;
`;

const CloseIcon = styled.i`
  width: 20px;
  width: 1.25rem;
  height: 19px;
  height: 1.1875rem;
  background: url(${GetStyledCompImg('common/ico_close04.png')}) no-repeat 0 0;
  display: inline-block;
  cursor: pointer;
`;

const AbsoluteCloseIcon = styled.i`
  width: 20px;
  width: 1.25rem;
  height: 19px;
  height: 1.1875rem;
  background: url(${GetStyledCompImg('common/ico_close04.png')}) no-repeat 0 0;
  display: inline-block;
  right: 30px;
  top: 30px;
  cursor: pointer;
  position: absolute;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: 24px 41px;
  padding-top: 1.5rem;
  padding-right: 2.5625rem;
  padding-bottom: 1.5rem;
  padding-left: 2.5625rem;
  padding: 1.5rem 2.5625rem;
`;

const PopupContent = styled.div`
  overflow: hidden;
  width: 100%;
  height: -webkit-calc(100% - 72px);
  height: calc(100% - 72px);
  display: table;
`;

const Title = styled.div`
  font-size: 24px;
  font-size: 1.5rem;
  font-family: 'SDGothicNeobTTF-eSm';
  text-align: left;
`;

interface Props {
  img: string;
  open: boolean;
  title?: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * 이미지 팝업을 위한 컴포넌트입니다.
 * 특정 이미지 클릭 시 확대 표출 시키는 용도로 사용됩니다.
 */
const ImgPopup = observer(({ img, open, title, setOpen }: Props) => {
  const [isDragging, setIsDragging] = useState(false);
  const { model: zoomModel } = useLocalObservable(() => ({ model: new ImgZoomModel() }));

  const handleClose = () => {
    setOpen(false);
    zoomModel.setInit(); // 초기화 (모든 좌표 0)
    zoomModel.setIsZoom(false);
  };

  useEffect(() => {
    const body = document.getElementsByTagName('body');

    if (open) {
      body[0].classList.add('burgerMenu-open');
    } else {
      body[0].classList.remove('burgerMenu-open');
    }
  }, [open]);

  const handleDragMove = e => {
    zoomModel.setIsMousePosition(zoomModel.x + e.movementX, zoomModel.y + e.movementY);
  };

  const handlePointerDown = e => {
    if (zoomModel.isZoom) {
      setIsDragging(true);
    }
  };

  const handlePointerUp = e => {
    if (!zoomModel.isZoom) {
      zoomModel.setIsZoom(true);
    } else {
      // 드래그 여부 평가
      // 1. 이전에 드래그가 이뤄진 경우
      if (zoomModel.isLastDrag()) {
        zoomModel.setSync(); // 동기화 (prev와 현재 좌표를 동일하게 맞춘다)
      }
      // 2. 드래그가 이뤄지지 않은 경우
      else {
        zoomModel.setInit(); // 초기화 (모든 좌표 0)
        zoomModel.setIsZoom(false);
      }
      setIsDragging(false);
    }
  };

  const handlePointerMove = e => {
    if (isDragging && zoomModel.isZoom) handleDragMove(e);
  };

  return open ? (
    <Popup open={open} type="simple" dialogSize="full" popState={`change-img-popup`}>
      <Container isZoom={zoomModel.isZoom}>
        <HeaderContainer>
          {title && <Title>{title}</Title>}

          {title ? <CloseIcon onClick={handleClose} /> : <AbsoluteCloseIcon onClick={handleClose} />}
        </HeaderContainer>

        <PopupContent onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerMove={handlePointerMove}>
          <ImgContainer /* onClick={handleClick} */ x={zoomModel.x} y={zoomModel.y} isZoom={zoomModel.isZoom}>
            <Img src={img} alt={img} />
          </ImgContainer>
        </PopupContent>
      </Container>
    </Popup>
  ) : null;
});

export default ImgPopup;
